import { useEffect, useRef, useMemo } from "react";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceX,
  forceY,
  Simulation,
  SimulationNodeDatum,
  SimulationLinkDatum,
} from "d3-force";
import {
  useReactFlow,
  ReactFlowState,
  useStore,
  Node,
  ReactFlowProps,
} from "@xyflow/react";

type UseForceLayoutOptions = {
  strength: number;
  distance: number;
  enabled: boolean;
};

type SimNodeType = SimulationNodeDatum & Node;
type SimEdgeType = SimulationLinkDatum<SimNodeType>;

type DragEvents = {
  start: ReactFlowProps["onNodeDragStart"];
  drag: ReactFlowProps["onNodeDrag"];
  stop: ReactFlowProps["onNodeDragStop"];
};

const elementCountSelector = (state: ReactFlowState) =>
  (state.nodeLookup?.size || 0) + state.edges.length;

const nodesInitializedSelector = (state: ReactFlowState) =>
  state.nodesInitialized;

function useForceLayout({
  strength = -1000,
  distance = 150,
  enabled = false,
}: UseForceLayoutOptions) {
  const elementCount = useStore(elementCountSelector);
  const nodesInitialized = useStore(nodesInitializedSelector);
  const { setNodes, getNodes, getEdges } = useReactFlow();

  const draggingNodeRef = useRef<null | Node>(null);
  const simulationRef = useRef<null | Simulation<SimNodeType, SimEdgeType>>(
    null,
  );

  const dragEvents = useMemo<DragEvents>(
    () => ({
      start: (_event, node) => (draggingNodeRef.current = node),
      drag: (_event, node) => (draggingNodeRef.current = node),
      stop: () => (draggingNodeRef.current = null),
    }),
    [],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const nodes = getNodes();
    const edges = getEdges();

    if (!nodes.length || !nodesInitialized) {
      return;
    }

    const simulationNodes: SimNodeType[] = nodes.map((node) => ({
      ...node,
      x: node.position.x,
      y: node.position.y,
    }));

    const simulationLinks: SimEdgeType[] = edges.map((edge) => edge);

    const simulation = forceSimulation<SimNodeType>()
      .nodes(simulationNodes)
      .force("charge", forceManyBody().strength(strength))
      .force(
        "link",
        forceLink<SimNodeType, SimEdgeType>(simulationLinks)
          .id((d) => d.id)
          .strength(0.05)
          .distance(distance),
      )
      .force("x", forceX().x(0).strength(0.08))
      .force("y", forceY().y(0).strength(0.15))
      .on("tick", () => {
        setNodes((nodes) =>
          nodes.map((node, i) => {
            const { x, y } = simulationNodes[i];
            const dragging = draggingNodeRef.current?.id === node.id;

            if (dragging) {
              simulationNodes[i].fx = node.position.x;
              simulationNodes[i].fy = node.position.y;
            } else {
              delete simulationNodes[i].fx;
              delete simulationNodes[i].fy;
            }

            return { ...node, position: { x: x ?? 0, y: y ?? 0 } };
          }),
        );
      });

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
      simulationRef.current = null;
    };
  }, [
    elementCount,
    getNodes,
    getEdges,
    setNodes,
    strength,
    distance,
    nodesInitialized,
    enabled,
  ]);

  return { dragEvents, simulation: simulationRef.current };
}

export default useForceLayout;
